import { Agent as HttpAgent } from 'http';
import { Agent as HttpsAgent } from 'https';
import { default as Axios } from 'axios';
import { default as axiosRetry } from 'axios-retry';
import deepmerge from 'deepmerge';
import isRetryAllowed from 'is-retry-allowed';
import { AxiosException } from './AxiosException';
export const SAFE_HTTP_METHODS = ['get', 'head', 'options'];
export const IDEMPOTENT_HTTP_METHODS = SAFE_HTTP_METHODS.concat(['put', 'delete']);
export const NON_IDEMPOTENT_HTTP_METHODS = ['post', 'patch'];
export function isRetryableResponseStatus(error) {
    const status = error.response?.status;
    return !!status && ([408, 429].includes(status) || (status >= 500 && status <= 599));
}
export function isRetryableErrorCode(error, retryAbortedRequest = false) {
    return !!error.code && isRetryAllowed(error) && (error.code !== 'ECONNABORTED' || retryAbortedRequest);
}
export function isNetworkError(error) {
    return (!error.response &&
        !!error.code);
}
export function isRetryableNetworkError(error, retryAbortedRequest) {
    return isNetworkError(error) && isRetryableErrorCode(error, retryAbortedRequest);
}
export function isIdempotentRequestError(error) {
    const method = error.config.method?.toLowerCase();
    return !!method && IDEMPOTENT_HTTP_METHODS.includes(method);
}
export function isRetryableIdempotentRequestError(error, retryAbortedRequest = true) {
    if (!isIdempotentRequestError(error)) {
        return false;
    }
    if (isNetworkError(error)) {
        return isRetryableNetworkError(error, retryAbortedRequest);
    }
    return isRetryableResponseStatus(error);
}
export function isNotIdempotentRequestError(error) {
    return !isIdempotentRequestError(error);
}
export function isRetryableNotIdempotentRequestError(error, retryAbortedRequest = false) {
    if (!isNotIdempotentRequestError(error)) {
        return false;
    }
    if (isNetworkError(error)) {
        return isRetryableNetworkError(error, retryAbortedRequest);
    }
    return false;
}
export function socketsInfo(sockets) {
    return Object.keys(sockets).map((host) => {
        return { host, socketsCount: sockets[host]?.length || 0 };
    });
}
function runShowSocketsInfoInterval(https, agent, ms) {
    setInterval(() => {
        console.log(`${new Date().toISOString()} HTTP${https ? 'S' : ''} agent: sockets: %j ||| freeSockets: %j`, socketsInfo(agent.sockets), socketsInfo(agent.freeSockets));
    }, Number.parseInt(`${ms}`, 10));
}
const httpAgent = new HttpAgent({ keepAlive: true });
const httpsAgent = new HttpsAgent({ keepAlive: true });
let httpAgentDebugIntervalEnabled = false;
let httpsAgentDebugIntervalEnabled = false;
export function createAxiosInstance(config = {}, interceptors = {}) {
    Axios.defaults.timeout = 15000;
    const axiosConfig = deepmerge({
        timeout: Axios.defaults.timeout,
        keepAlive: true,
    }, config.axiosConfig || {});
    const axiosRetryConfig = deepmerge({
        retries: 6,
        shouldResetTimeout: true,
        retryDelay: axiosRetry.exponentialDelay,
        retryAbortedRequest: {
            forIdempotent: true,
            forNonIdempotent: false,
        },
    }, config.axiosRetryConfig || {});
    if (typeof process !== 'undefined' && process.release?.name === 'node' && !!axiosConfig.keepAlive) {
        if (!axiosConfig.httpAgent) {
            axiosConfig.httpAgent = httpAgent;
            if (!httpAgentDebugIntervalEnabled && process.env.HTTP_AGENT_DEBUG_ENABLED === 'true') {
                httpAgentDebugIntervalEnabled = true;
                runShowSocketsInfoInterval(false, httpAgent, process.env.HTTP_AGENT_DEBUG_SHOW_SOCKET_INFO_INTERVAL || 1000);
            }
        }
        if (!axiosConfig.httpsAgent) {
            axiosConfig.httpsAgent = httpsAgent;
            if (!httpsAgentDebugIntervalEnabled && process.env.HTTPS_AGENT_DEBUG_ENABLED === 'true') {
                httpsAgentDebugIntervalEnabled = true;
                runShowSocketsInfoInterval(true, httpsAgent, process.env.HTTPS_AGENT_DEBUG_SHOW_SOCKET_INFO_INTERVAL || 1000);
            }
        }
    }
    const axios = Axios.create(axiosConfig);
    axiosRetry(axios, deepmerge({
        retryCondition: (err) => {
            return (isRetryableIdempotentRequestError(err, axiosRetryConfig?.retryAbortedRequest?.forIdempotent) ||
                isRetryableNotIdempotentRequestError(err, axiosRetryConfig?.retryAbortedRequest?.forNonIdempotent));
        },
    }, axiosRetryConfig));
    ['request', 'response'].forEach((type) => (interceptors[type] || []).forEach((interceptor) => {
        axios.interceptors[type].use(interceptor.onFulfilled, interceptor.onRejected);
    }));
    axios.interceptors.response.use((response) => {
        return response;
    }, async (error) => {
        return Promise.reject(new AxiosException(error));
    });
    return axios;
}
//# sourceMappingURL=axios.js.map